import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';

const STATE_KEY = 'dubbletrack:playback:state';
const CMD_KEY = 'dubbletrack:playback:cmd';
const CHANNEL_NAME = 'dubbletrack-playback';

// Prefer window.name as a stable identity (e.g. 'dubbletrack-player' for the player
// page). Falls back to a random ID for tabs that don't set window.name.
const TAB_ID = window.name || Math.random().toString(36).slice(2, 10);

/**
 * SyncChannel
 *
 * Cross-tab playback coordination via localStorage and BroadcastChannel.
 * Owns the transport wiring, tab identity, reactive remote state, and
 * command execution — so PlaybackService only deals with stereo events.
 *
 * Reactive properties (usable in templates/getters):
 *   remoteIsPlaying   — boolean | null (null = no info yet)
 *   remoteUrl         — URL playing in another tab
 *   remoteTabId       — ID of the tab that last reported state (persists after pause)
 *   remoteWindowName  — window.name of that tab (for focus/bring-to-front)
 *   isPlayingRemotely — true if another tab is actively playing
 *   isPlayingLocally  — true if this tab's stereo is playing
 *   isPlayingAnywhere — either local or remote is playing
 *
 * Tab registry (best-effort — not updated for hard crashes/kills):
 *   openTabs              — Map<tabId, windowName> of tabs seen since this tab opened
 *   isTabOpen(windowName) — true if a tab with that window.name is in the registry
 *   tabIdForWindowName(n) — returns the tabId for a named window, or null
 */
export class SyncChannel {
  _channel = null;
  _storageHandler = null;
  _stereo = null;

  // Best-effort registry of currently-open tabs. Populated via BroadcastChannel
  // register/unregister messages. Not reliable for hard crashes or killed processes.
  openTabs = new TrackedMap(); // tabId -> windowName

  // WindowProxy kept from a window.open() call. When present and not closed,
  // focus() on it is guaranteed to work (opener relationship).
  _playerWindowRef = null;

  @tracked remoteIsPlaying = null;
  @tracked remoteUrl = null;
  @tracked remoteTabId = null;
  @tracked remoteWindowName = null;

  constructor({ stereo } = {}) {
    this._stereo = stereo;

    // Seed from localStorage immediately so state is accurate before any
    // BroadcastChannel or storage events have had a chance to fire.
    let saved = this.savedState;
    if (saved) {
      this.remoteIsPlaying = saved.isPlaying;
      this.remoteUrl = saved.url;
      if (saved.tabId) this.remoteTabId = saved.tabId;
      if (saved.windowName) this.remoteWindowName = saved.windowName;
    }

    this._storageHandler = this._handleStorageEvent.bind(this);
    window.addEventListener('storage', this._storageHandler);

    if (typeof BroadcastChannel !== 'undefined') {
      this._channel = new BroadcastChannel(CHANNEL_NAME);
      this._channel.onmessage = (event) => this._handleChannelMessage(event.data);

      // Announce ourselves to other tabs.
      this._channel.postMessage({ type: 'register', tabId: TAB_ID, windowName: window.name || null });

      // Unregister on close (best-effort — won't fire on hard crash/kill).
      window.addEventListener('beforeunload', () => {
        this._channel?.postMessage({ type: 'unregister', tabId: TAB_ID });
      });

      // Ask any open tabs to re-broadcast their live state so we can initialize
      // with something fresher than what's in localStorage.
      Promise.resolve().then(() => {
        this._channel?.postMessage({ type: 'request-state' });
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Reactive getters
  // ---------------------------------------------------------------------------

  get isPlayingRemotely() {
    return this.remoteIsPlaying === true;
  }

  get isPlayingLocally() {
    return this._stereo.currentSound?.isPlaying ?? false;
  }

  get isPlayingAnywhere() {
    return this.isPlayingLocally || this.isPlayingRemotely;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Write state to localStorage and broadcast via BroadcastChannel.
   * tabId and timestamp are added automatically.
   * @param {{ isPlaying: boolean, url: string, windowName: string|null }} state
   */
  broadcastState(state) {
    let payload = { ...state, tabId: TAB_ID, at: Date.now() };
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(payload));
    } catch (e) {}
    this._channel?.postMessage({ type: 'state', ...payload });
  }

  /**
   * Send a command to other tabs.
   * @param {'pause'|'play'|'toggle'} command
   * @param {string} [targetTabId] - If provided, only that tab will act on it.
   */
  sendCommand(command, targetTabId = null) {
    let payload = { command, at: Date.now() };
    if (targetTabId) payload.targetTabId = targetTabId;
    try {
      localStorage.setItem(CMD_KEY, JSON.stringify(payload));
    } catch (e) {}
    this._channel?.postMessage({ type: 'command', ...payload });
  }

  /**
   * Returns true if a tab with the given window.name is currently registered.
   * Best-effort — won't reflect hard crashes or killed processes.
   * @param {string} windowName
   * @returns {boolean}
   */
  isTabOpen(windowName) {
    for (let [, name] of this.openTabs) {
      if (name === windowName) return true;
    }
    return false;
  }

  /**
   * Returns the tabId for the first registered tab with the given window.name,
   * or null if none is found.
   * @param {string} windowName
   * @returns {string|null}
   */
  tabIdForWindowName(windowName) {
    for (let [tabId, name] of this.openTabs) {
      if (name === windowName) return tabId;
    }
    return null;
  }

  destroy() {
    window.removeEventListener('storage', this._storageHandler);
    this._channel?.close();
    this._channel = null;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  get savedState() {
    try {
      return JSON.parse(localStorage.getItem(STATE_KEY));
    } catch (e) {
      return null;
    }
  }

  _applyState({ isPlaying, url, tabId, windowName }) {
    this.remoteIsPlaying = isPlaying;
    this.remoteUrl = url;
    // Keep tabId/windowName even after the remote tab pauses — we still need
    // to know who to send the play command or focus request back to.
    if (tabId) this.remoteTabId = tabId;
    if (windowName) this.remoteWindowName = windowName;
  }

  _executeCommand({ command }) {
    let sound = this._stereo.currentSound;
    if (command === 'focus') {
      window.focus();
      return;
    }
    if (!sound) return;
    if (command === 'pause' && sound.isPlaying) {
      sound.pause();
    } else if (command === 'play' && !sound.isPlaying) {
      sound.play();
    } else if (command === 'toggle') {
      sound.togglePause();
    }
  }

  _replyWithCurrentState() {
    let sound = this._stereo.currentSound;
    if (sound) {
      this.broadcastState({
        isPlaying: sound.isPlaying,
        url: sound.identifier,
        windowName: window.name || null,
      });
    }
    // Re-announce registration so newly opened tabs can populate their openTabs map.
    this._channel?.postMessage({ type: 'register', tabId: TAB_ID, windowName: window.name || null });
  }

  _handleStorageEvent(event) {
    if (event.key === STATE_KEY && event.newValue) {
      try {
        this._applyState(JSON.parse(event.newValue));
      } catch (e) {}
    } else if (event.key === CMD_KEY && event.newValue) {
      try {
        let cmd = JSON.parse(event.newValue);
        if (!cmd.targetTabId || cmd.targetTabId === TAB_ID) {
          this._executeCommand(cmd);
        }
      } catch (e) {}
    }
  }

  _handleChannelMessage(data) {
    if (!data) return;
    if (data.type === 'state') {
      this._applyState(data);
    } else if (data.type === 'command') {
      if (!data.targetTabId || data.targetTabId === TAB_ID) {
        this._executeCommand(data);
      }
    } else if (data.type === 'request-state') {
      this._replyWithCurrentState();
    } else if (data.type === 'register') {
      this.openTabs.set(data.tabId, data.windowName);
    } else if (data.type === 'unregister') {
      this.openTabs.delete(data.tabId);
    }
  }
}
